import { useCallback } from 'react';
import { ApolloQueryResult, gql, useQuery } from '@apollo/client';
import { last } from 'lodash';
import {
  Contact,
  ContactWhereInput,
  Query,
  QueryContactsArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { getNodesFromConnection } from 'src/utils/graphql/get-nodes-from-connection';

export type ContactsRefetchType = (
  variables?: Partial<Partial<QueryContactsArgs>> | undefined,
) => Promise<ApolloQueryResult<Pick<Query, 'contacts'>>>;

type UseContactsReturn = {
  loading: boolean;
  contacts: Contact[];
  refetch: ContactsRefetchType;
  loadMore: (variables?: Partial<Partial<QueryContactsArgs>>) => Promise<void>;
  hasNextPage?: boolean;
};

export const GET_CONTACTS = gql`
  query GetContacts(
    $institutionId: Int!
    $where: ContactWhereInput
    $after: String
    $first: Int
  ) {
    contacts(institutionId: $institutionId, where: $where, after: $after, first: $first)
      @connection(key: "infinite-scroll") {
      edges {
        cursor
        node {
          id
          label
          fullName
          primaryEmail
          primaryPhone {
            national
          }
          secondaryPhone {
            national
          }
          interest
          stage {
            id
            name
            slug
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const UseContacts = (where?: ContactWhereInput): UseContactsReturn => {
  const currentInstitution = useCurrentInstitution();
  const { data, loading, refetch, fetchMore } = useQuery<
    Pick<Query, 'contacts'>,
    Partial<QueryContactsArgs>
  >(GET_CONTACTS, {
    variables: {
      institutionId: currentInstitution.id,
      where,
    },
  });

  const loadMore = useCallback(async () => {
    const lastCursor = last(data?.contacts.edges)?.cursor;

    if (!lastCursor) {
      return;
    }

    await fetchMore({
      variables: {
        after: lastCursor,
        first: 100,
        where,
      },
    });
  }, [data?.contacts.edges, fetchMore, where]);

  return {
    contacts: getNodesFromConnection(data?.contacts),
    loading,
    refetch,
    loadMore,
    hasNextPage: data?.contacts?.pageInfo.hasNextPage,
  };
};
